import React, { useState } from 'react'
import { Box } from '@mui/material'
import { DndContext, DragEndEvent, DragOverlay, DragStartEvent } from '@dnd-kit/core'
import { DashboardConfig, DashboardLayout } from '../../../types/gotypes'
import { resolveActiveLayout } from '../../../utils/dashboard'
import {
  CellRef,
  MIN_CELL_WIDTH_PCT,
  distributeWidthsEvenly,
  duplicateWidgetAt,
  emptyLayout,
  insertWidgetAtDropTarget,
  moveWidgetToDropTarget,
  redistributeWidths,
  removeWidgetAt,
  setRowWidths,
} from '../../../utils/dashboardBuilderLayout'
import { calculateCellWidths } from '../../../utils/dashboard'
import { LayoutDragData, layoutCollisionDetection, readDragData, readDropTarget, useLayoutDragSensors } from '../layout/layoutDnd'
import { WIDGET_TYPE_OPTIONS } from '../widgetTaxonomy'
import WidgetPalette from '../layout/WidgetPalette'
import BuilderCanvas from './BuilderCanvas'
import BuilderTabs from './BuilderTabs'
import { WidgetPaletteDragGhost, WidgetDragGhost } from '../layout/WidgetPaletteCard'

interface DashboardLayoutBuilderProps {
  config: DashboardConfig
  onConfigChange: (config: DashboardConfig) => void
  /** Every placeholder that carries a job reference is seeded with this — the
   *  dashboard's job is fixed at creation (#961), so the builder never shows
   *  a job picker. */
  job: string
  isManager?: boolean
}

/**
 * The Phase 1 layout builder (#971): a controlled component wrapping the
 * widget palette, the tab bar, and the drag-and-drop canvas around a
 * `DashboardConfig`. It never invents a new layout primitive — every drag
 * ends by calling straight into the pure `dashboardBuilderLayout` functions,
 * which map onto the same Page -> Row -> Cell model `DashboardEditor` reads
 * and writes. That's what lets "Create Dashboard" hand off to the existing
 * editor with zero translation.
 */
const DashboardLayoutBuilder: React.FC<DashboardLayoutBuilderProps> = ({ config, onConfigChange, job, isManager }) => {
  const [activePageIndex, setActivePageIndex] = useState(0)
  const [activeDrag, setActiveDrag] = useState<LayoutDragData | null>(null)
  const sensors = useLayoutDragSensors()

  const pages = config.pages || []
  const clampedPageIndex = pages.length === 0 ? 0 : Math.min(activePageIndex, pages.length - 1)
  const activeLayout = resolveActiveLayout(config, clampedPageIndex)

  const commitLayout = (layout: DashboardLayout) => {
    if (pages.length === 0) {
      onConfigChange({ ...config, layout })
      return
    }
    const newPages = pages.map((p, i) => (i === clampedPageIndex ? { ...p, layout } : p))
    onConfigChange({ ...config, pages: newPages })
  }

  const handleDragStart = (event: DragStartEvent) => {
    setActiveDrag(readDragData(event.active.data.current) || null)
  }

  const handleDragCancel = () => setActiveDrag(null)

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDrag(null)
    const { active, over } = event
    if (!over) return
    const source = readDragData(active.data.current)
    const target = readDropTarget(over.data.current)
    if (!source || !target) return

    if (source.source === 'palette') {
      commitLayout(insertWidgetAtDropTarget(activeLayout, target, source.widgetType, job))
    } else {
      commitLayout(moveWidgetToDropTarget(activeLayout, source.ref, target))
    }
  }

  // #1049 — the builder writes the same `display_props.width` the dashboard
  // editor writes, through the same pure `redistributeWidths`.
  const handleResizeBoundary = (rowIndex: number, boundaryIndex: number, deltaPct: number) => {
    const row = activeLayout.rows[rowIndex]
    if (!row) return
    const widths = redistributeWidths(calculateCellWidths(row.cells), boundaryIndex, deltaPct, MIN_CELL_WIDTH_PCT)
    commitLayout(setRowWidths(activeLayout, rowIndex, widths))
  }

  const handleDistributeEvenly = (rowIndex: number) => {
    const row = activeLayout.rows[rowIndex]
    if (!row) return
    commitLayout(setRowWidths(activeLayout, rowIndex, distributeWidthsEvenly(row.cells.length)))
  }

  const handleDuplicateCell = (ref: CellRef) => commitLayout(duplicateWidgetAt(activeLayout, ref))
  const handleDeleteCell = (ref: CellRef) => commitLayout(removeWidgetAt(activeLayout, ref))
  const handleDeleteRow = (rowIndex: number) =>
    commitLayout({ ...activeLayout, rows: activeLayout.rows.filter((_, i) => i !== rowIndex) })

  const handleAddPage = () => {
    const newPage = { id: crypto.randomUUID(), name: `Page ${pages.length + 1}`, layout: emptyLayout() }
    onConfigChange({ ...config, pages: [...pages, newPage] })
    setActivePageIndex(pages.length)
  }

  const handleRenamePage = (index: number, name: string) => {
    onConfigChange({ ...config, pages: pages.map((p, i) => (i === index ? { ...p, name } : p)) })
  }

  const handleDeletePage = (index: number) => {
    const newPages = pages.filter((_, i) => i !== index)
    if (newPages.length === 0) return
    onConfigChange({ ...config, pages: newPages })
    if (clampedPageIndex >= newPages.length) setActivePageIndex(newPages.length - 1)
    else if (clampedPageIndex === index && index > 0) setActivePageIndex(index - 1)
  }

  // Palette drags keep the type-card ghost; canvas moves ghost as a titled
  // card naming the dragged widget (#1129) — WidgetDragGhost resolves icon and
  // fallback label through dragGhostOption, so 'table_graph' charts/tables
  // always get a ghost (#1130).
  const paletteDragOption = activeDrag?.source === 'palette' ? WIDGET_TYPE_OPTIONS[activeDrag.widgetType] : undefined
  const draggedCanvasCell = activeDrag?.source === 'canvas'
    ? activeLayout.rows[activeDrag.ref.rowIndex]?.cells[activeDrag.ref.cellIndex]
    : undefined

  return (
    <DndContext sensors={sensors} collisionDetection={layoutCollisionDetection} onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragCancel={handleDragCancel}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
        <BuilderTabs
          pages={pages}
          activePageIndex={clampedPageIndex}
          onSelect={setActivePageIndex}
          onAdd={handleAddPage}
          onRename={handleRenamePage}
          onDelete={handleDeletePage}
        />
        <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
          <WidgetPalette isManager={isManager} />
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            <BuilderCanvas
              layout={activeLayout}
              onDuplicateCell={handleDuplicateCell}
              onDeleteCell={handleDeleteCell}
              onDeleteRow={handleDeleteRow}
              onResizeBoundary={handleResizeBoundary}
              onDistributeEvenly={handleDistributeEvenly}
            />
          </Box>
        </Box>
      </Box>
      <DragOverlay>
        {paletteDragOption ? <WidgetPaletteDragGhost widget={paletteDragOption} />
          : draggedCanvasCell ? <WidgetDragGhost cell={draggedCanvasCell} />
          : null}
      </DragOverlay>
    </DndContext>
  )
}

export default DashboardLayoutBuilder
